import {Component, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs/internal/Subscription";
import {FormBuilder, FormGroup} from "@angular/forms";
import {toastrService} from "../../../../services/ux/toastr.service";
import {WalletService} from "../../../../services/wallet.service";
import {ActivatedRoute} from "@angular/router";
import {ModalPaymentService} from "../../../../services/ux/modal-payment.service";
import {LocalStorage} from "../../../../services/local-storage";
import {finalize} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {KindergardenHttpClientService} from "../../../../data/kindergarden-http-client.service";
import {Kindergarden} from "../../../../models/SearchService/kindergarden.model";
import { TranslateService } from "@ngx-translate/core";

@Component({
    templateUrl: "./kindergarden.component.html"
})
export class KindergardenComponent implements OnDestroy {
    // region Subscriptions
    subscription: Subscription;
    ccSubscription: Subscription;
    finishTransactionSubscription: Subscription;
    // endregion

    searchForm: FormGroup;

    isLoading = false;

    hasParam = false;

    hasResults = false;

    private _kindergardenRecords: Kindergarden[];
    public get kindergardenRecords(): Kindergarden[] {
        return this._kindergardenRecords;
    }

    public set kindergardenRecords(value: Kindergarden[]) {
        this._kindergardenRecords = value;
        this.hasResults = !!value.length;
    }

    // * payment
    selectedInvoice: Kindergarden = {} as Kindergarden;
    invoiceIndex: number;

    constructor(
        private fb: FormBuilder,
        private toastr: toastrService,
        private walletService: WalletService,
        private activeRoute: ActivatedRoute,
        private modalService: ModalPaymentService,
        private ls: LocalStorage,
        private kindergardenHttpClient: KindergardenHttpClientService,
        private translateService: TranslateService
    ) {
        this.searchForm = this.fb.group({
            firstName: [""],
            lastName: [""],
            type: [1]
        });

        this.subscription = walletService.$walletReadyEmitter.subscribe(
            (isReady: boolean) => {
                if (isReady) {
                    this.processKindergardenTransaction();
                }
            }
        );

        this.ccSubscription = walletService.$creditCardReadyEmitter.subscribe(
            (ccSelected: boolean) => {
                if (ccSelected) {
                    this.processKindergardenTransaction(ccSelected);
                }
            }
        );

        this.finishTransactionSubscription = walletService.$transactionFinish.subscribe(
            (value) => {
                if (value) {
                    this.removeFinishedTransactionFromRecords();
                }
            }
        );

        this.activeRoute.params.subscribe((params: {
            firstName: string,
            lastName: string,
            serviceid: string
        }) => {
            if (!(params.firstName || params.lastName || params.serviceid)) {
                return;
            }

            this.fillSearchFromQueryParams(params);

            this.hasParam = true;
            this.onSearch();
        });
    }

    fillSearchFromQueryParams(params) {
        this.searchForm.controls["firstName"].setValue(params.firstName);
        this.searchForm.controls["firstName"].disable();
        this.searchForm.controls["lastName"].setValue(params.lastName);
        this.searchForm.controls["lastName"].disable();
        this.searchForm.controls["type"].setValue(params.serviceid);
        this.searchForm.controls["type"].disable();
    }

    onSearch() {
        if (!this.searchForm.value.firstName || !this.searchForm.value.lastName || !this.searchForm.value.type) {
            return this.toastr.warning(this.translateService.instant("Services.FillInTheFields"));
        }

        this.isLoading = true;

        this.kindergardenHttpClient
            .search(this.searchForm.value.firstName,
                this.searchForm.value.lastName,
                this.searchForm.value.type
            )
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response: Kindergarden[]) => {
                    this.kindergardenRecords = response;
                },
                error: (error: HttpErrorResponse) => {
                    this.kindergardenRecords = [] as Kindergarden[];
                    if (error.error === "invalid_parameters") {
                        this.toastr.warning(this.translateService.instant("Services.FillInTheFields"));
                    } else if (error.error === "no_invoices") {
                        this.toastr.warning(this.translateService.instant("Services.NoInvoicesFound"));
                    } else if (error.error === "search_fail") {
                        this.toastr.warning(this.translateService.instant("Services.SearchFailed"));
                    } else if (error.error === "contract_dont_exists") {
                        this.toastr.warning(this.translateService.instant("Services.SearchFailed"));
                    }
                }
            });
    }

    onPay(index: number) {
        this.invoiceIndex = index;
        this.selectedInvoice = this.kindergardenRecords[index];

        this.walletService.selectInvoice(this.selectedInvoice);
        this.walletService.amountOfTransaction = this.selectedInvoice.amount + this.selectedInvoice.komision;

        this.modalService.open();
    }

    processKindergardenTransaction(creditCardMethod: boolean = false) {
        const kindergardenInvoice = {
            invoice: this.walletService.getInvoice(),
            ipAddress: "127.0.0.1",
            deviceId: "EternaWebPosta"
        };

        if (creditCardMethod) {
            this.kindergardenHttpClient
                .PayCC(kindergardenInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: (response) => {
                        if (!response.data) {
                            this.walletService.emitTransactionProgress({
                                    isSuccess: false,
                                    error: response.error.message
                                }
                            );
                            return;
                        }
                        this.walletService.emitTransactionProgress(
                            {
                                isSuccess: true,
                                error: null
                            }
                        );
                        this.ls.set("sn", "kindergarden");

                        window.location = response.data.redirect;
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject = {
                            isSuccess: false,
                            error: error.error.message
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    }
                });
        } else {
            this.kindergardenHttpClient
                .PayWallet(kindergardenInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: () => {
                        const emmiterObject = {
                            isSuccess: true,
                            error: null
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject = {
                            isSuccess: false,
                            error: error.error.message
                        };
                        this.walletService.emitTransactionProgress(emmiterObject);
                    }
                });
        }
    }

    private removeFinishedTransactionFromRecords() {
        this.kindergardenRecords.splice(this.invoiceIndex, 1);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.ccSubscription.unsubscribe();
        this.finishTransactionSubscription.unsubscribe();
    }
}
